/**
 * BaseTablePagination – pagination strip for table views.
 * Styled to match legacy Bootstrap btn-primary pagination buttons.
 */

import React, { useState, useEffect } from 'react'

export interface BaseTablePaginationProps {
    /** Start index of current page (1-based for display) */
    showingFrom: number
    /** End index of current page (1-based) */
    showingTo: number
    /** Total number of records */
    total: number
    /** Current page size (e.g. 10); shown in the middle pill when provided */
    pageSize?: number
    /** Whether the previous page is available */
    hasPrev?: boolean
    /** Whether the next page is available */
    hasNext?: boolean
    /** Go to first page */
    onFirst?: () => void
    /** Go to previous page */
    onPrev?: () => void
    /** Go to next page */
    onNext?: () => void
    /** Go to last page */
    onLast?: () => void
    /** Optional class name for the strip */
    className?: string
    /** Optional id for the strip */
    id?: string
}

export function BaseTablePagination({
    showingFrom,
    showingTo,
    total,
    pageSize,
    hasPrev = false,
    hasNext = false,
    onFirst,
    onPrev,
    onNext,
    onLast,
    className,
    id,
}: BaseTablePaginationProps) {
    const [limitValue, setLimitValue] = useState(pageSize != null ? String(pageSize) : '')
    useEffect(() => {
        if (pageSize != null) setLimitValue(String(pageSize))
    }, [pageSize])

    const statusText = `Showing ${showingFrom} to ${showingTo} of ${total}`

    return (
        <span id={id} className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ marginRight: '6px', paddingRight: '20px', color: '#555', fontSize: '13px' }}>{statusText}</span>
            {onFirst && (
                <span
                    className={`btn btn-primary btn_prev_next${!hasPrev ? ' disabled' : ''}`}
                    onClick={hasPrev ? onFirst : undefined}
                >
                    «
                </span>
            )}
            {onPrev && (
                <span
                    className={`btn btn-primary capitalize btn_prev_next${!hasPrev ? ' disabled' : ''}`}
                    onClick={hasPrev ? onPrev : undefined}
                >
                    ← prev
                </span>
            )}
            {pageSize != null && (
                <input
                    type="text"
                    className="w-12 px-2 rounded-lg border-[3px] border-solid border-white h-[30px]"
                    style={{ marginTop: '0' }}
                    value={limitValue}
                    onChange={(e) => setLimitValue(e.target.value)}
                    onKeyUp={(e) => {
                        const el = e.currentTarget
                        const v = parseInt(el.value)
                        if (!isNaN(v) && (window as any).limit_change) (window as any).limit_change(el)
                    }}
                />
            )}
            {onNext && (
                <span
                    className={`btn btn-primary capitalize btn_prev_next${!hasNext ? ' disabled' : ''}`}
                    onClick={hasNext ? onNext : undefined}
                >
                    next →
                </span>
            )}
            {onLast && (
                <span
                    className={`btn btn-primary btn_prev_next${!hasNext ? ' disabled' : ''}`}
                    onClick={hasNext ? onLast : undefined}
                >
                    »
                </span>
            )}
        </span>
    )
}
